import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { Button, Card, Detail, EmptyState, ErrorState, PageHeader, Spinner, Table } from "@/components/ui";
import { AgencyMark } from "@/components/agency-mark";

/**
 * The landlord statement.
 *
 * **Collected, less the costs the agency met on their behalf, equals what is due — and there is no
 * commission line, which is the whole correction.** The rent is the landlord's, whole and
 * untouched; the agency's income is the fee the tenant paid on top of it, on its own line of the
 * same invoice, reaching the agency's own statement rather than being deducted from this one. A
 * deduction here took money that was never the agency's and understated what the owner was owed.
 * See ADR-0131.
 *
 * A recharged expense survives that correction and is not the same thing: it is a bill the agency
 * has **already paid** on the owner's behalf, with a receipt behind it, being reimbursed out of the
 * owner's own money — money that moved, rather than a percentage asserted against a total.
 *
 * Printable, because a landlord's accountant will ask for it on paper.
 */

interface StatementResponse {
  landlord: { id: string; name: string; email: string | null; phone: string | null; bankName: string | null; bankAccountNumber: string | null } | null;
  from: string;
  to: string;
  properties: Array<{ id: string; name: string }>;
  collected: number;
  expenses: number;
  net: number;
  lines: Array<{ date: string; reference: string; description: string; propertyName: string; gross: number }>;
  expenseLines: Array<{ date: string; description: string; category: string; amount: number; propertyName: string }>;
  agency: { name: string; phone: string | null; email: string | null };
}

export function StatementPage() {
  const { landlordId } = useParams();
  const [params, setParams] = useSearchParams();
  /* The whole session: the mark on a landlord statement is the agency's own, and the statement
     payload carries only its name and contacts. */
  const session = useSession();
  const { vocab } = session;
  const fmt = useFormat();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["statement", landlordId, params.get("from"), params.get("to")],
    queryFn: () =>
      api.get<StatementResponse>(`/reports/statement/${landlordId}`, {
        from: params.get("from") ?? undefined,
        to: params.get("to") ?? undefined,
      }),
    enabled: Boolean(landlordId),
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorState error={error} retry={() => refetch()} />;

  return (
    <div>
      <PageHeader
        title={`Statement · ${data.landlord?.name ?? "Landlord"}`}
        back={{ to: `/app/clients/${landlordId}`, label: data.landlord?.name ?? "Client" }}
        subtitle={`${fmt.date(data.from)} to ${fmt.date(data.to)}`}
        actions={<Button variant="secondary" className="no-print" onClick={() => window.print()}>Print</Button>}
      />

      <form
        className="mb-5 flex flex-wrap gap-2 no-print"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          setParams({ from: String(form.get("from") ?? ""), to: String(form.get("to") ?? "") });
        }}
      >
        <input name="from" type="date" defaultValue={data.from} className="field max-w-40" />
        <input name="to" type="date" defaultValue={data.to} className="field max-w-40" />
        <Button type="submit" variant="secondary">Change period</Button>
      </form>

      {data.properties.length === 0 ? (
        <Card><EmptyState title={`No ${vocab.properties} are linked to this ${vocab.landlord}.`} /></Card>
      ) : (
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="space-y-5 lg:col-span-2">
            <Card padded={false} className="printable">
              <div className="border-b border-ink-200 p-4 sm:p-5">
                {/* Compact: this header is one line above a long table, so the mark is smaller
                    here than on an invoice. The treatment is still the shared one. */}
                <AgencyMark name={data.agency.name} logoUrl={session.org?.logoUrl} size="compact">
                  <p className="text-xs text-ink-500">Statement for {data.landlord?.name}</p>
                </AgencyMark>
              </div>
              {data.lines.length === 0 ? (
                <EmptyState title="Nothing was collected in this period." />
              ) : (
                <Table
                  head={
                    <tr>
                      <th>Date</th>
                      <th>Receipt</th>
                      <th>Detail</th>
                      <th className="num">Collected</th>
                    </tr>
                  }
                >
                  {data.lines.map((line, index) => (
                    <tr key={index}>
                      <td className="text-ink-500">{fmt.date(line.date)}</td>
                      <td className="text-ink-500">{line.reference}</td>
                      <td>
                        {line.description}
                        <span className="block text-xs text-ink-400">{line.propertyName}</span>
                      </td>
                      <td className="num tabular">{fmt.money(line.gross)}</td>
                    </tr>
                  ))}
                </Table>
              )}
            </Card>

            {data.expenseLines.length > 0 ? (
              <Card padded={false}>
                <div className="p-4 sm:p-5"><p className="text-sm font-semibold">Expenses deducted</p></div>
                <Table head={<tr><th>Date</th><th>Category</th><th>Detail</th><th className="num">Amount</th></tr>}>
                  {data.expenseLines.map((line, index) => (
                    <tr key={index}>
                      <td className="text-ink-500">{fmt.date(line.date)}</td>
                      <td>{line.category}</td>
                      <td className="text-ink-500">
                        {line.description}
                        <span className="block text-xs text-ink-400">{line.propertyName}</span>
                      </td>
                      <td className="num tabular">−{fmt.money(line.amount)}</td>
                    </tr>
                  ))}
                </Table>
              </Card>
            ) : null}
          </div>

          <div className="space-y-5">
            <Card>
              <p className="text-sm font-semibold">Summary</p>
              <dl className="mt-2">
                <Detail label="Collected">{fmt.money(data.collected)}</Detail>
                {/* No commission line, and the absence is the point rather than an omission: what
                    was collected for this owner is theirs. ADR-0131. */}
                <Detail label="Costs recharged">−{fmt.money(data.expenses)}</Detail>
                <Detail label="Due to you">
                  <span className="text-base font-semibold text-brand-700">{fmt.money(data.net)}</span>
                </Detail>
              </dl>
              {data.landlord?.bankAccountNumber ? (
                <p className="mt-3 text-xs text-ink-500">
                  To be paid to {data.landlord.bankName} · {data.landlord.bankAccountNumber}
                </p>
              ) : null}
            </Card>

            <Card>
              <p className="text-sm font-semibold">{titleCase(vocab.properties)}</p>
              <ul className="mt-2 space-y-1 text-sm text-ink-600">
                {/* A rate was printed beside each name and there is none to print. The list says
                    which buildings this statement covers, which is what a reader checks it for. */}
                {data.properties.map((property) => (
                  <li key={property.id}>{property.name}</li>
                ))}
              </ul>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
